import {
  DropdownButtonType,
  List,
  ListItem,
  LocallyFilteringList,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  RadioLayoutType,
  SingleSelectDropdown,
  TextAreaWidget,
  TextFieldWidget,
} from '@tableau/widgets';
import React from 'react';

import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { L10n } from '~src/ts/L10n/L10n';
import { IProjectInfo } from '~src/ts/models/IProjectInfo';

import { Messages } from '../../codegen/Localize';
import { cssClass } from '../../style';
import { IConnectedApplicationInfo } from '../models/ConnectedApplicationInfo';
import { ConnectedApplications } from '../resources/ConnectedApplicationsResource';
import { CreateConnectedApplicationResult } from '../serverApi/requests/CreateConnectedApplication';
import { UpdateConnectedApplicationResult } from '../serverApi/requests/UpdateConnectedApplication';

export namespace ConnectedApplicationDialogTestIds {
  export const NameInput = 'name-input';
  export const ScopeLabel = 'scope-label';
  export const ScopeAll = 'scope-all';
  export const ScopeSingleProject = 'scope-single-project';
  export const DomainButtons = 'domain-buttons';
  export const DomainAll = 'domain-all';
  export const DomainSpecified = 'domain-specified';
  export const DomainTextArea = 'domain-text-area';
}

export type ConnectedApplicationDialogContentProps = {
  projects: IProjectInfo[];
  previous?: IConnectedApplicationInfo;
};

export type ConnectedApplicationDialogContentState = Partial<IConnectedApplicationInfo>;

// Confusing, but the props for the whole dialog, not just the dialog content
export type ConnectedApplicationDialogProps = ReactActionDialogBodyProps<
  ConnectedApplicationDialogContentProps,
  ConnectedApplicationDialogContentState
>;

// Necessary because the SingleSelectDropdown insists on the elements having an underlying value
// exported only because the tests need to know about it
export enum AccessScope {
  All = 'All',
  Project = 'Project',
}

export function ConnectedApplicationDialogContent(props: ConnectedApplicationDialogProps): JSX.Element {
  const scopeListItems: ListItem[] = [
    {
      content: Messages.connectedApplicationsScopeAllProjects(),
      testId: ConnectedApplicationDialogTestIds.ScopeAll,
      value: AccessScope.All,
    },
    {
      content: Messages.connectedApplicationsScopeSingleProject(),
      testId: ConnectedApplicationDialogTestIds.ScopeSingleProject,
      value: AccessScope.Project,
    },
  ];
  const isAllProjectsSelected = props.dialogState.projectId === undefined;
  const projectListItems = props.projects.map(project => ({
    // Content is limited to 50 char length due to it making the UI look weird otherwise
    content: L10n.elideIfLong(project.name, 50),
    tooltip: project.name,
    value: project.luid,
  }));

  // By default the filtering list would filter on value (project ID), but we want to filter on name
  // We use the tooltip instead of content because the content string may be elided if the name is long
  const projectFilterFn = (item: ListItem, filter: string) => item.tooltip?.toLowerCase().includes(filter.toLowerCase());

  // The size of the Access row is a function of whether the All Project option is selected or not due to the extra dropdown
  const accessRowHeight = isAllProjectsSelected ? '55px' : '100px';

  const gridStyle = cssClass(cssPrefix, 'grid', {
    display: 'grid',
    gridTemplateColumns: '190px 375px',
    gap: '10',
    gridAutoRows: `40px ${accessRowHeight} 150px`,
    paddingTop: 20,
    textAlign: 'left',
  });

  const domainSafelistOptions: RadioButtonWithLabelOption<string | undefined>[] = [
    {
      label: Messages.connectedApplicationsAllowAllDomains(),
      testId: ConnectedApplicationDialogTestIds.DomainAll,
      value: 'true', // true === All Domains aka. unrestricted embedding
    },
    {
      label: Messages.connectedApplicationsAllowOnlySelectedDomains(),
      testId: ConnectedApplicationDialogTestIds.DomainSpecified,
      value: 'false', // false === Restricted embedding
    },
  ];

  // When we create the app for the first time, we want to default unrestrictedEmbedding to true
  if (props.dialogState.unrestrictedEmbedding === undefined) {
    props.dialogState.unrestrictedEmbedding = true;
  }
  const isAllDomainsSelected = props.dialogState.unrestrictedEmbedding;
  return <div className={gridStyle}>
    <div id={ConnectedApplicationDialogTestIds.NameInput} className={nameLabelCellStyle}>
      {Messages.connectedApplicationsName()}
    </div>
    <div className={nameValueCellStyle}>
      <TextFieldWidget
        aria-labelledby={ConnectedApplicationDialogTestIds.NameInput}
        label=''
        testId={ConnectedApplicationDialogTestIds.NameInput}
        onChange={event => props.updateDialogState({ name: event.target.value })}
        style={{ width: 375 }}
        value={props.dialogState.name}
      />
    </div>
    <div className={scopeLabelCellStyle} data-tb-test-id={ConnectedApplicationDialogTestIds.ScopeLabel}>
      {Messages.connectedApplicationsAccess()}
    </div>
    <div className={scopeValueCellStyle}>
      <SingleSelectDropdown
        label={Messages.connectedApplicationsScope()}
        list={<List items={scopeListItems} />}
        onChange={(value) => {
          value === AccessScope.All
            ? props.updateDialogState({ projectId: undefined })
            // Empty string is an invalid project ID but also not undefined so we know the user still needs to select a project
            : props.updateDialogState({ projectId: '' });
        }}
        type={DropdownButtonType.Outline}
        value={isAllProjectsSelected ? AccessScope.All : AccessScope.Project}
      />
      {!isAllProjectsSelected && <SingleSelectDropdown
        label={Messages.connectedApplicationsProject()}
        list={<LocallyFilteringList items={projectListItems} itemFilterFn={projectFilterFn} />}
        type={DropdownButtonType.Outline}
        onChange={projectId => props.updateDialogState({ projectId })}
        value={props.dialogState.projectId}
      />}
    </div>
    <div className={domainsLabelCellStyle} id={ConnectedApplicationDialogTestIds.DomainButtons}>
      {Messages.connectedApplicationsDomainSafelist()}
    </div>
    <div className={domainsValueCellStyle}>
      <RadioButtonWithLabelGroupWidget
        buttonList={domainSafelistOptions}
        checkedIndex={isAllDomainsSelected ? 0 : 1}
        handleChange={unrestrictedEmbedding => props.updateDialogState({ unrestrictedEmbedding: unrestrictedEmbedding === 'true' })}
        label=''
        aria-labelledby={ConnectedApplicationDialogTestIds.DomainButtons}
        layout={RadioLayoutType.Veritical}
      />
      <TextAreaWidget
        label=''
        aria-labelledby={ConnectedApplicationDialogTestIds.DomainButtons}
        testId={ConnectedApplicationDialogTestIds.DomainTextArea}
        value={props.dialogState.domainSafelist}
        disabled={isAllDomainsSelected}
        placeholder={Messages.connectedApplicationsDomainsPlaceholder()}
        onChange={event => props.updateDialogState({ domainSafelist: event.target.value })}
        style={{ width: 375, height: 110, resize: 'none' }}
      />
    </div>
  </div>;
}

function actionDisabled(props: ConnectedApplicationDialogContentProps, state: ConnectedApplicationDialogContentState): boolean {
  const invalidName = state.name === undefined || state.name === '';
  // undefined is a valid project value, it means all projects
  const invalidProject = state.projectId === '';

  // If previous is defined and we haven't changed anything from it then we shouldn't enable updating
  const isUnchanged = props.previous
    && props.previous.name === state.name
    && props.previous.projectId === state.projectId
    && props.previous.domainSafelist === state.domainSafelist;
  return invalidName || invalidProject || isUnchanged;
}

function action(props: ConnectedApplicationDialogContentProps, state: ConnectedApplicationDialogContentState): Promise<CreateConnectedApplicationResult | UpdateConnectedApplicationResult> {
  /*make sure the domain safelist we're passing to the backend is space-delimited
  we collapse all whitespaces and \n chars to a single space, so if an user types "1 2" in the same line, it will be treated as 2 separate domains
  if we decide to change the behavior to treat it as "12" (1 domain), we will modify this regex
  */
  const domains = state.domainSafelist?.replace(/\s+/g, ' ');
  if (state.id) {
    return ConnectedApplications.update({
      enabled: true,
      // This seems like it's useless but we're telling the compiler we KNOW this will not be undefined
      // we know this because the action can only be dispatched if it's not disabled, and we disable it if name is undefined
      name: state.name,
      clientId: state.id,
      projectId: (state.projectId === undefined) ? '' : state.projectId,
      domainSafelist: domains,
      unrestrictedEmbedding: state.unrestrictedEmbedding
    });
  }
  return ConnectedApplications.create({
    enabled: false,
    // This seems like it's useless but we're telling the compiler we KNOW this will not be undefined
    // we know this because the action can only be dispatched if it's not disabled, and we disable it if name is undefined
    name: state.name,
    projectId: state.projectId,
    domainSafelist: domains,
    unrestrictedEmbedding: state.unrestrictedEmbedding
  });
}

export async function openConnectedApplicationDialog(projects: IProjectInfo[], previous?: IConnectedApplicationInfo): Promise<CreateConnectedApplicationResult> {
  const initialDialogState = { ...previous };
  const initialBodyProps = { projects };
  const modal = openReactActionDialog({
    title: previous
      ? Messages.connectedApplicationsEditConnectedApp()
      : Messages.connectedApplicationsSetupNewConnectedApp(),
    bodyComponent: ConnectedApplicationDialogContent,
    dialogWidth: 600,
    initialDialogState,
    initialBodyProps,
    actionName: previous
      ? Messages.update()
      : Messages.create(),
    action,
    actionDisabled,
    dismissDialogOnActionFailed: true,
    initiallyFocusedElement: (dialog => dialog.querySelector('input')),
  });
  return modal.result;
}

const cssPrefix = 'ConnectedApplicationDialog';

const nameLabelCellStyle = cssClass(cssPrefix, 'NameLabel', {
  gridArea: '1 / 1 / 2 / 2',
});

const nameValueCellStyle = cssClass(cssPrefix, 'NameValue', {
  gridArea: '1 / 2 / 2 / 3',
});

const scopeLabelCellStyle = cssClass(cssPrefix, 'ScopeLabel', {
  gridArea: '2 / 1 / 3 / 2',
});

const scopeValueCellStyle = cssClass(cssPrefix, 'ScopeValue', {
  gridArea: '2 / 2 / 3 / 3',
});

const domainsLabelCellStyle = cssClass(cssPrefix, 'DomainLabel', {
  gridArea: '3 / 1 / 4 / 2',
});

const domainsValueCellStyle = cssClass(cssPrefix, 'DomainValue', {
  gridArea: '3 / 2 / 4 / 3',
});

